import * as React from 'react';
import { useContext, useEffect, useState } from 'react';
import useSWR from 'swr';
import { fetcher } from '../app/lib/fetcher';
import { PlanContext } from '../app/providers/UpgradePlanContext';
import { useRouter } from 'next/router';
import { Track } from '../app/models/track';
import { Album } from '../app/models/album';
import { formatNumber, formatCompact } from '../app/utils/currency';
import moment from 'moment';
import { ToastContext } from '../app/providers/ToastContext';
import { SheetContext } from '../app/providers/SheetContext';
import UploadMediaModal from '../app/components/upload/UploadMediaModal';
import RevenueChart from '../app/components/dashboard/RevenueChart';
import QuickActions from '../app/components/dashboard/QuickActions';
import DraftsList from '../app/components/dashboard/DraftsList';
import SetupGuide from '../app/components/dashboard/SetupGuide';
import useOnboarding from '../app/hooks/useOnboarding';
import { useAppBridge } from '@shopify/app-bridge-react';
import { userLoggedInFetch } from './_app';
// Import Polaris web component types
import '../types/polaris-web-components.d';

interface DashboardStats {
  window: { days: number; from: string; to: string };
  revenue: {
    total: number;
    previousTotal: number;
    changePct: number | null;
    currency: string;
    series: { date: string; amount: number }[];
  };
  orders: { total: number; previousTotal: number; changePct: number | null; pendingCount: number };
  downloads: { total: number; previousTotal: number; changePct: number | null };
  catalog: {
    publishedTracks: number;
    publishedAlbums: number;
    draftTracks: number;
    draftAlbums: number;
  };
  topResources: {
    resourceID: string;
    type: string;
    title: string;
    thumbnail: string;
    units: number;
    revenue: number;
  }[];
  recentOrders: {
    id: string;
    orderNumber: string | number;
    email: string;
    amount: number;
    currency: string;
    status: string;
    createdAt: string;
  }[];
}

const PLACEHOLDER_ART = '/static/images/artwork-placeholder.png';

/** Session-scoped "Remind me later" for the v1→v2 migration banner. */
const MIGRATION_DISMISS_KEY = 'tb-migration-banner-dismissed';

/** Session-scoped: whether the merchant expanded the migration banner into the full guide. */
const MIGRATION_EXPANDED_KEY = 'tb-migration-steps-expanded';

const Home: React.FC = () => {
  const app = useAppBridge();
  const { data: dashboard } = useSWR<any>(`${process.env.APP_URL}/api/dashboard`, (input) =>
    fetcher(app, input),
  );
  const { data: stats } = useSWR<DashboardStats>(
    `${process.env.APP_URL}/api/dashboard/stats`,
    (input) => fetcher(app, input),
  );
  const { guide: onboarding, loading: onboardingLoading } = useOnboarding();
  const { show, plan } = useContext(PlanContext);
  const { show: showToast } = useContext(ToastContext);
  const { show: showSheet } = useContext(SheetContext);
  const router = useRouter();
  const [initLoading, setInitLoading] = useState(true);
  const [trackLoading, setTrackLoading] = useState(false);
  const [albumLoading, setAlbumLoading] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [migrationDismissed, setMigrationDismissed] = useState(
    () => typeof window !== 'undefined' && sessionStorage.getItem(MIGRATION_DISMISS_KEY) === '1',
  );
  const [migrationExpanded, setMigrationExpanded] = useState(
    () => typeof window !== 'undefined' && sessionStorage.getItem(MIGRATION_EXPANDED_KEY) === '1',
  );

  useEffect(() => {
    if (initLoading && dashboard !== undefined && stats !== undefined) {
      setInitLoading(false);
    }
  }, [initLoading, dashboard, stats]);

  // A failed onboarding request leaves `onboarding` undefined, which deliberately falls through
  // to the normal dashboard, since a broken endpoint must never cost a merchant their page.
  // Established v1 merchants (legacy theme assets, app block not yet placed) keep their full
  // dashboard — they are prompted to migrate via the banner below, not bounced into the setup
  // gate meant for brand-new stores.
  const onboardingGating = !!onboarding && !onboarding.complete && !onboarding.legacy;
  const booting = initLoading || onboardingLoading;

  const createTrack = async () => {
    const request = {
      method: 'POST',
      headers: {
        accept: 'application/json',
        'Content-Type': 'application/json',
      },
    };
    setTrackLoading(true);
    const result = await userLoggedInFetch(app)(`${process.env.APP_URL}/api/tracks`, request);
    setTrackLoading(false);
    if (result.status === 402) {
      return show({
        content: `You're now on <b>${plan.currentPlan?.name}</b> plan. This plan has a limit of <b>${plan.currentPlan?.tracks.limit} tracks</b>. Please upgrade to continue.`,
      });
    }
    if (result.status !== 201) {
      const body = await result.json();
      return showToast({ message: body.message, isError: true });
    }

    const newTrack = await result.json();
    router.push('/catalog/tracks/[id]', `/catalog/tracks/${newTrack._id}`);
  };

  const createAlbum = async () => {
    const request = {
      method: 'POST',
      headers: {
        accept: 'application/json',
        'Content-Type': 'application/json',
      },
    };
    setAlbumLoading(true);
    const result = await userLoggedInFetch(app)(`${process.env.APP_URL}/api/albums`, request);
    setAlbumLoading(false);
    if (result.status === 402) {
      return show({
        content: `You're now on <b>${plan.currentPlan?.name}</b> plan. This plan has a limit of <b>${plan.currentPlan?.albums.limit} albums</b>. Please upgrade to continue.`,
      });
    }
    if (result.status !== 201) {
      const body = await result.json();
      return showToast({ message: body.message, isError: true });
    }

    const newAlbum = await result.json();
    router.push('/catalog/albums/[id]', `/catalog/albums/${newAlbum._id}`);
  };

  const navigate = (route: string, as: string) => (e?: any) => {
    e?.preventDefault?.();
    router.push(route, as);
  };

  // ── KPI strip ──────────────────────────────────────────────────────────────
  const changeBadge = (changePct: number | null) => {
    if (changePct === null || changePct === undefined) {
      return <s-badge tone='neutral'>New</s-badge>;
    }
    const positive = changePct >= 0;
    return (
      <s-badge tone={positive ? 'success' : 'critical'}>
        {`${positive ? '▲' : '▼'} ${Math.abs(changePct)}%`}
      </s-badge>
    );
  };

  const kpiCard = (label: string, value: React.ReactNode, badge: React.ReactNode, sub?: string) => (
    <s-box padding='large' borderRadius='base' background='subdued'>
      <s-stack direction='block' gap='small'>
        <s-text color='subdued'>{label}</s-text>
        <s-stack direction='inline' gap='small' alignItems='center'>
          <s-heading>{value}</s-heading>
          {badge}
        </s-stack>
        <s-text color='subdued'>{sub || `vs. previous ${stats?.window.days ?? 30} days`}</s-text>
      </s-stack>
    </s-box>
  );

  const kpiStripMarkup = stats && (
    <s-grid gridTemplateColumns='repeat(4, minmax(0, 1fr))' gap='base'>
      {kpiCard(
        'Revenue',
        formatNumber(stats.revenue.total, stats.revenue.currency),
        changeBadge(stats.revenue.changePct),
      )}
      {kpiCard(
        'Orders',
        formatCompact(stats.orders.total),
        changeBadge(stats.orders.changePct),
        stats.orders.pendingCount > 0
          ? `${stats.orders.pendingCount} awaiting fulfillment`
          : `vs. previous ${stats.window.days} days`,
      )}
      {kpiCard(
        'Downloads',
        formatCompact(stats.downloads.total),
        changeBadge(stats.downloads.changePct),
      )}
      {kpiCard(
        'Published catalog',
        formatCompact(stats.catalog.publishedTracks + stats.catalog.publishedAlbums),
        null,
        `${stats.catalog.publishedTracks} tracks · ${stats.catalog.publishedAlbums} albums`,
      )}
    </s-grid>
  );

  // ── Revenue trend ────────────────────────────────────────────────────────────
  const revenueSectionMarkup = stats && (
    <s-section heading={`Revenue · last ${stats.window.days} days`}>
      <s-stack direction='block' gap='base'>
        <s-stack direction='inline' gap='small' alignItems='center'>
          <s-heading>{formatNumber(stats.revenue.total, stats.revenue.currency)}</s-heading>
          {changeBadge(stats.revenue.changePct)}
        </s-stack>
        <RevenueChart series={stats.revenue.series} currency={stats.revenue.currency} />
      </s-stack>
    </s-section>
  );

  // ── Top sellers ──────────────────────────────────────────────────────────────
  const resourceRoute = (type: string) =>
    type === 'album' ? '/catalog/albums/[id]' : '/catalog/tracks/[id]';

  const topSellersMarkup = stats && (
    <s-section heading='Top sellers'>
      {stats.topResources.length === 0 ? (
        <div className='dash-empty'>
          <s-text color='subdued'>No sales in this period yet.</s-text>
        </div>
      ) : (
        <s-table variant='table'>
          <s-table-header-row>
            <s-table-header listSlot='primary'>Title</s-table-header>
            <s-table-header>Units</s-table-header>
            <s-table-header>Revenue</s-table-header>
          </s-table-header-row>
          <s-table-body>
            {stats.topResources.map((r) => (
              <s-table-row key={`${r.type}-${r.resourceID}`}>
                <s-table-cell>
                  <s-stack direction='inline' gap='small' alignItems='center'>
                    <s-thumbnail src={r.thumbnail || PLACEHOLDER_ART} alt={r.title} size='small' />
                    <s-stack direction='block' gap='none'>
                      <s-link
                        href={`/catalog/${r.type === 'album' ? 'albums' : 'tracks'}/${
                          r.resourceID
                        }`}
                        onClick={navigate(
                          resourceRoute(r.type),
                          `/catalog/${r.type === 'album' ? 'albums' : 'tracks'}/${r.resourceID}`,
                        )}
                      >
                        <s-text type='strong'>{r.title || 'Untitled'}</s-text>
                      </s-link>
                      <s-badge>{r.type === 'album' ? 'Album' : 'Track'}</s-badge>
                    </s-stack>
                  </s-stack>
                </s-table-cell>
                <s-table-cell>
                  <s-text>{formatCompact(r.units)}</s-text>
                </s-table-cell>
                <s-table-cell>
                  <s-text type='strong'>{formatNumber(r.revenue, stats.revenue.currency)}</s-text>
                </s-table-cell>
              </s-table-row>
            ))}
          </s-table-body>
        </s-table>
      )}
    </s-section>
  );

  // ── Recent orders ────────────────────────────────────────────────────────────
  const orderStatusTone = (status: string) => {
    switch (status) {
      case 'Fulfilled':
        return 'success';
      case 'Pending':
        return 'caution';
      case 'Error':
        return 'critical';
      default:
        return 'neutral';
    }
  };

  const recentOrdersMarkup = stats && (
    <s-section heading='Recent orders'>
      <s-stack direction='block' gap='base'>
        {stats.orders.pendingCount > 0 && (
          <s-banner tone='caution'>
            You have {stats.orders.pendingCount} order
            {stats.orders.pendingCount === 1 ? '' : 's'} awaiting fulfillment.
          </s-banner>
        )}
        {stats.recentOrders.length === 0 ? (
          <div className='dash-empty'>
            <s-text color='subdued'>No orders yet.</s-text>
          </div>
        ) : (
          <s-table variant='table'>
            <s-table-header-row>
              <s-table-header listSlot='primary'>Order</s-table-header>
              <s-table-header>Status</s-table-header>
              <s-table-header>Total</s-table-header>
              <s-table-header listSlot='secondary'>Date</s-table-header>
            </s-table-header-row>
            <s-table-body>
              {stats.recentOrders.map((order) => (
                <s-table-row key={order.id}>
                  <s-table-cell>
                    <s-link
                      href={`/orders/${order.id}`}
                      onClick={navigate('/orders/[id]', `/orders/${order.id}`)}
                    >
                      <s-stack direction='block' gap='none'>
                        <s-text type='strong'>#{order.orderNumber}</s-text>
                        <s-text color='subdued'>{order.email || 'No email'}</s-text>
                      </s-stack>
                    </s-link>
                  </s-table-cell>
                  <s-table-cell>
                    <s-badge tone={orderStatusTone(order.status)}>{order.status}</s-badge>
                  </s-table-cell>
                  <s-table-cell>
                    <s-text type='strong'>{formatNumber(order.amount, order.currency)}</s-text>
                  </s-table-cell>
                  <s-table-cell>
                    <s-text color='subdued'>
                      {order.createdAt ? moment(order.createdAt).format('MMM D') : '—'}
                    </s-text>
                  </s-table-cell>
                </s-table-row>
              ))}
            </s-table-body>
          </s-table>
        )}
      </s-stack>
    </s-section>
  );

  // ── Finish your drafts ───────────────────────────────────────────────────────
  const openDraft = (draft: Album | Track) => {
    if (draft.resource === 'album') {
      router.push('/catalog/albums/[id]', `/catalog/albums/${draft._id}`);
    } else {
      router.push('/catalog/tracks/[id]', `/catalog/tracks/${draft._id}`);
    }
  };

  const draftsMarkup = !initLoading && dashboard && (
    <s-section heading='Finish your drafts'>
      <DraftsList drafts={dashboard.drafts || []} onOpen={openDraft} />
    </s-section>
  );

  // ── Quick actions ────────────────────────────────────────────────────────────
  const quickActionsMarkup = (
    <s-section heading='Quick actions'>
      <QuickActions
        actions={[
          {
            key: 'track',
            icon: 'play',
            title: 'Add a new track',
            description: 'Create a track to sell in your store.',
            cta: 'Create track',
            tone: 'violet',
            loading: trackLoading,
            onClick: createTrack,
          },
          {
            key: 'album',
            icon: 'categories',
            title: 'Create an album',
            description: 'Sell a collection of tracks together.',
            cta: 'Create album',
            tone: 'blue',
            loading: albumLoading,
            onClick: createAlbum,
          },
          {
            key: 'license',
            icon: 'page-report',
            title: 'Setup licensing',
            description: 'Define the terms for the music you sell.',
            cta: 'Setup terms',
            tone: 'green',
            onClick: () => router.push('/licenses'),
          },
        ]}
      />
    </s-section>
  );

  // ── Layout ───────────────────────────────────────────────────────────────────
  // Until every setup step verifies, the guide IS the page: a dashboard of empty KPIs teaches
  // a new merchant nothing, and burying the one blocking task under it is how stores end up
  // live with no player on any product. The gate lifts the moment onboarding.complete flips.
  // v1→v2 migration nudge, shown above the intact dashboard. The server only populates
  // `migration` for an established v1 shop that hasn't placed the app block. The banner is
  // deliberately just awareness + a way in: its one-click predecessor dropped merchants into
  // the theme editor with no checklist, so instead it reveals the full SetupGuide inline (see
  // migrationSteps) where every step, the "press Save" reminder and Recheck live.
  // "Remind me later" and the expanded state are both session-scoped: the banner returns next
  // login and the whole prompt disappears for good once the block is detected as placed.
  const migration = onboarding?.migration;
  const migrationBanner = migration && !migrationDismissed && (
    <s-banner tone='warning' heading={migration.title}>
      <s-stack direction='block' gap='small'>
        <s-text>{migration.description}</s-text>
        {migration.warning && <s-text>{migration.warning}</s-text>}
        <s-stack direction='inline' gap='small'>
          {!migrationExpanded && (
            <s-button
              variant='primary'
              onClick={() => {
                sessionStorage.setItem(MIGRATION_EXPANDED_KEY, '1');
                setMigrationExpanded(true);
              }}
            >
              Show me how
            </s-button>
          )}
          <s-button
            variant='tertiary'
            onClick={() => {
              sessionStorage.setItem(MIGRATION_DISMISS_KEY, '1');
              setMigrationDismissed(true);
            }}
          >
            Remind me later
          </s-button>
        </s-stack>
      </s-stack>
    </s-banner>
  );

  // The full step-by-step guide, revealed under the banner once the merchant clicks "Show me
  // how". Non-gating — the dashboard stays intact below. The guide's place-player-block step
  // carries the real theme-editor deep link (and, on a vintage theme, the OS 2.0 warning), and
  // hides itself the moment every step verifies.
  const migrationSteps = migration && !migrationDismissed && migrationExpanded && (
    <s-stack direction='block' gap='base'>
      <SetupGuide />
    </s-stack>
  );

  const onboardingLayout = onboardingGating && (
    <s-stack direction='block' gap='base'>
      <SetupGuide />
    </s-stack>
  );

  const loadedLayout = !booting && !onboardingGating && (
    <s-stack direction='block' gap='base'>
      {kpiStripMarkup}
      {revenueSectionMarkup}
      <s-grid gridTemplateColumns='1fr 1fr' gap='base'>
        <s-grid-item>{topSellersMarkup}</s-grid-item>
        <s-grid-item>{recentOrdersMarkup}</s-grid-item>
      </s-grid>
      <s-grid gridTemplateColumns='2fr 1fr' gap='base'>
        <s-grid-item>{quickActionsMarkup}</s-grid-item>
        <s-grid-item>{draftsMarkup}</s-grid-item>
      </s-grid>
    </s-stack>
  );

  const skeletonLayout = booting && !onboardingGating && (
    <s-stack direction='block' gap='base'>
      <s-grid gridTemplateColumns='repeat(4, minmax(0, 1fr))' gap='base'>
        {[0, 1, 2, 3].map((i) => (
          <s-box key={i} padding='large' borderRadius='base' background='subdued'>
            <s-stack direction='block' gap='small' alignItems='center' justifyContent='center'>
              <s-spinner size='base' />
            </s-stack>
          </s-box>
        ))}
      </s-grid>
      <s-section>
        <s-stack direction='block' gap='base' alignItems='center' justifyContent='center'>
          <s-spinner size='large' />
          <s-text color='subdued'>Loading dashboard...</s-text>
        </s-stack>
      </s-section>
    </s-stack>
  );

  return (
    <s-page heading='Dashboard' inlineSize={onboardingGating ? 'small' : 'large'}>
      <s-button
        slot='primary-action'
        variant='primary'
        icon='upload'
        onClick={() => setUploadOpen(true)}
      >
        Upload new media
      </s-button>
      <s-button slot='secondary-actions' onClick={() => showSheet({})}>
        Media library
      </s-button>
      {migrationBanner}
      {migrationSteps}
      {onboardingLayout}
      {skeletonLayout}
      {loadedLayout}
      {uploadOpen && <UploadMediaModal visible onClose={() => setUploadOpen(false)} />}
    </s-page>
  );
};

export default Home;
